import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Checkbox,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
    EmbeddedCheckout,
    EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { axiosInstance } from '../../helpers/axios'
import { stripePromise } from '../../helpers/stripe'
import {
    formatAddressDisplay,
    formatInternationalAddressDisplay,
} from './workbookFieldUtils'
import {
    InternationalAddressPayload,
    WorkbookAddressField,
} from './WorkbookAddressField'
import { ApplicantWorkbookStepper } from './ApplicantWorkbookStepper'
import {
    firstOpenSectionIndex,
    isLastAvailableSection,
    lastRequiredIndex,
    nextAvailableIndex,
    prevAvailableIndex,
    sectionIsAvailable,
    sectionIsComplete,
    sectionIsEditable,
} from './workbookStepper'

function parseDateAnswer(value: unknown): Date | null {
    if (typeof value !== 'string' || !value.trim()) return null
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
    if (!match) return null
    const date = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
    )
    return Number.isNaN(date.getTime()) ? null : date
}

function formatDateAnswer(date: Date | null): string {
    if (!date || Number.isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function answerHasValue(value: unknown): boolean {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return Boolean(value.trim())
    if (typeof value === 'boolean') return true
    if (typeof value === 'number') return !Number.isNaN(value)
    if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
        if (
            typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value) &&
            Object.prototype.hasOwnProperty.call(value, 's3_key')
        ) {
            return Boolean(
                String((value as { s3_key?: unknown }).s3_key ?? '').trim(),
            )
        }
        return Object.keys(value as object).length > 0
    }
    return true
}

function fileAnswerName(value: unknown): string {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return ''
    const row = value as { file_name?: unknown; s3_key?: unknown }
    const name = String(row.file_name ?? '').trim()
    if (name) return name
    const key = String(row.s3_key ?? '').trim()
    if (!key) return ''
    return key.split('/').pop() || 'Uploaded photo'
}

const LEGAL_MIDDLE_NAME_FIELD_ID = 'legal_middle_name'
const NO_LEGAL_MIDDLE_NAME_FIELD_ID = 'no_legal_middle_name'
const GUARANTOR_MIDDLE_NAME_FIELD_ID = 'guarantor_middle_name'
const NO_GUARANTOR_MIDDLE_NAME_FIELD_ID = 'no_guarantor_middle_name'
const PREFERRED_PREMIUMS_FIELD_ID = 'preferred_premiums'
const PREFERRED_ROOMMATES_FIELD_ID = 'preferred_roommates'

const MIDDLE_NAME_CERT_PAIRS = [
    {
        nameId: LEGAL_MIDDLE_NAME_FIELD_ID,
        certId: NO_LEGAL_MIDDLE_NAME_FIELD_ID,
        missingMessage:
            'Please enter your middle name, or certify you do not have a legal middle name.',
    },
    {
        nameId: GUARANTOR_MIDDLE_NAME_FIELD_ID,
        certId: NO_GUARANTOR_MIDDLE_NAME_FIELD_ID,
        missingMessage:
            "Please enter the guarantor's middle name, or certify they do not have a legal middle name.",
    },
] as const
const HAS_PETS_FIELD_ID = 'has_pets'
const PET_ANIMAL_TYPE_FIELD_ID = 'pet_animal_type'
const PET_BREED_FIELD_ID = 'pet_breed'
const PET_DETAILS_FIELD_ID = 'pet_details'
const PET_NAME_FIELD_ID = 'pet_name'
const PET_WEIGHT_FIELD_ID = 'pet_weight'
const PET_PICTURE_FIELD_ID = 'pet_picture'
const PARKING_NEEDED_FIELD_ID = 'parking_needed'
const VEHICLE_DEPENDENT_FIELD_IDS = [
    'vehicle_make',
    'vehicle_model',
    'vehicle_color',
    'vehicle_license_plate',
    'vehicle_license_plate_state',
    'vehicle_info',
] as const
const HAS_PETS_HELPER_TEXT =
    'Do not include service or assistance animals. Those are handled separately.'
const PET_ANIMAL_TYPE_HELPER_TEXT =
    'Choose from the animal types this property allows.'
const PET_BREED_HELPER_TEXT =
    'If this breed is not listed, it is not an allowed breed at this property.'
const PET_WEIGHT_HELPER_TEXT = 'Pounds'
const PET_NOT_ALLOWED_HELPER_TEXT =
    'This property does not allow pets. Select No unless you have a service or assistance animal, which is handled separately.'

type RoommateEntry = {
    name: string
    email: string
    phone: string
}

function asPremiumIdList(value: unknown): number[] {
    if (!Array.isArray(value)) return []
    const ids: number[] = []
    for (const item of value) {
        const parsed = Number(item)
        if (Number.isInteger(parsed)) ids.push(parsed)
    }
    return ids
}

function asRoommateList(value: unknown): RoommateEntry[] {
    if (!Array.isArray(value)) return []
    return value
        .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
        .map((item) => ({
            name: String(item.name ?? ''),
            email: String(item.email ?? ''),
            phone: String(item.phone ?? ''),
        }))
}

function listAnswerIsComplete(value: unknown): boolean {
    return Array.isArray(value)
}

function certifiesNoMiddleName(
    answers: Record<string, unknown>,
    certId: string,
): boolean {
    return answers[certId] === true
}

function middleNamePairForField(fieldId: string) {
    return (
        MIDDLE_NAME_CERT_PAIRS.find(
            (pair) => pair.nameId === fieldId || pair.certId === fieldId,
        ) ?? null
    )
}

interface ResolvedApplicantField {
    field_id: string
    question_text: string
    input_type: string
    required: boolean
    options?: string[]
    helper_text?: string
    show_if?: {
        depends_on_field_id: string
        equals: string
    }
}

interface ResolvedApplicantSection {
    id: string
    label: string
    summary?: string
    required: boolean
    available?: boolean
    complete?: boolean
    editable?: boolean
    fields: ResolvedApplicantField[]
}

interface WorkbookApplicationResponse {
    status: string
    answers: Record<string, unknown>
    /** field_id → InternationalAddress row for address-type answers */
    resolved_addresses?: Record<string, InternationalAddressPayload>
    workbook_snapshot?: {
        buckets: Array<{
            bucket_id: string
            enabled: boolean
            fields: Array<{
                field_id: string
                enabled: boolean
                required: boolean
                input_type?: string
            }>
        }>
    }
    progress: {
        percent: number
        identity_complete: boolean
        workbook_complete: boolean
        lifestyle_complete?: boolean
        buckets?: Array<{
            bucket_id: string
            complete: boolean
        }>
    }
    applicant_data?: {
        step_id?: string | null
        step_gated?: boolean
        available?: string[]
        required?: string[]
        sections?: ResolvedApplicantSection[]
    }
    options?: {
        unit_configs: Array<{
            id: string
            name: string
            bed_count?: number | null
            bath_count?: number | null
        }>
        universities: Array<{
            id: string
            name: string
        }>
        qualifying_methods?: Array<{
            id: string
            label: string
        }>
        fallback_qualifying_methods?: Array<{
            id: string
            label: string
        }>
        fallback_choice_mode?: 'applicant' | 'property_manager'
        guarantor_relationships?: Array<{
            id: string
            label: string
        }>
        premiums?: Array<{
            id: number
            name: string
            description?: string
            amount?: string
        }>
        allowed_animal_types?: Array<{
            id: string
            label: string
        }>
        allowed_dog_breeds?: Array<{
            id: string
            label: string
        }>
    }
    complete_at?: string | null
    submitted_ip?: string
    has_submission_pdf?: boolean
    application_fee_cents?: number
    application_fee_paid?: boolean
    application_fee_gate?:
        | 'before_form'
        | 'before_sign'
        | 'after_signatures'
        | 'never'
    application_fee_requires_checkout?: boolean
    application_fee_blocks_form?: boolean
    application_fee_paid_at?: string | null
    remainder_due?: 'before_sign' | 'before_lease' | 'after_lease' | 'never' | null
    remainder_open?: boolean
    lifestyle_editable?: boolean
}

const LIFESTYLE_MATCH_FIELD_IDS = new Set([
    'study_habits',
    'sleep_schedule',
    'cleanliness',
    'noise_tolerance',
    'social_preference',
])

const LIFESTYLE_IMPORTANCE_OPTIONS = [
    { value: 'must', label: 'Must' },
    { value: 'prefer', label: 'Prefer' },
    { value: 'none', label: 'No preference' },
] as const

function lifestyleImportanceKey(fieldId: string): string {
    return `${fieldId}__importance`
}

function isLifestyleMatchField(fieldId: string): boolean {
    return LIFESTYLE_MATCH_FIELD_IDS.has(fieldId)
}

function LifestyleImportanceRadios({
    fieldId,
    value,
    onChange,
}: {
    fieldId: string
    value: string
    onChange: (next: string) => void
}) {
    return (
        <FormControl
            component="fieldset"
            sx={{ mt: 0.75, mb: 0.5 }}
        >
            <FormLabel
                sx={{
                    color: '#8b949e',
                    fontSize: '0.75rem',
                    '&.Mui-focused': { color: '#8b949e' },
                }}
            >
                How important is this to you?
            </FormLabel>
            <RadioGroup
                row
                name={lifestyleImportanceKey(fieldId)}
                value={value || 'prefer'}
                onChange={(event) => onChange(event.target.value)}
            >
                {LIFESTYLE_IMPORTANCE_OPTIONS.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={
                            <Radio
                                size="small"
                                sx={{
                                    color: '#8b949e',
                                    '&.Mui-checked': { color: '#46b083' },
                                }}
                            />
                        }
                        label={option.label}
                        sx={{
                            color: '#fff',
                            mr: 2,
                            '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
                        }}
                    />
                ))}
            </RadioGroup>
        </FormControl>
    )
}

interface ApplicantWorkbookProps {
    leadId: string
    remainderOnly?: boolean
}

type WorkbookMode = 'edit' | 'review' | 'checkout' | 'waiting'

function formatApplicationFee(feeCents: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(feeCents / 100)
}

const currentYear = new Date().getFullYear()

const GUARANTOR_FIELD_IDS = [
    'guarantor_first_name',
    'guarantor_middle_name',
    'no_guarantor_middle_name',
    'guarantor_last_name',
    'guarantor_email',
    'guarantor_phone',
    'guarantor_relationship',
] as const

const GUARANTOR_FIELD_ID_SET = new Set<string>(GUARANTOR_FIELD_IDS)

const QUALIFYING_METHOD_SELF = 'self_qualified'
const QUALIFYING_METHOD_GUARANTOR = 'personal_guarantor'

const SELF_QUALIFY_HELPER =
    'It is unusual to self-qualify. Only select Self if you are sure you have strong credit and monthly income that can cover the rent on your own.'

const FALLBACK_QUALIFYING_METHODS = [
    { id: QUALIFYING_METHOD_SELF, label: 'Self' },
    { id: QUALIFYING_METHOD_GUARANTOR, label: 'Guarantor' },
]

type QualifyingMethodOption = { id: string; label: string }

function qualifyingMethodChoices(
    fieldId: string,
    options: WorkbookApplicationResponse['options'],
): QualifyingMethodOption[] {
    if (fieldId === 'fallback_qualifying_method') {
        return options?.fallback_qualifying_methods?.length
            ? options.fallback_qualifying_methods
            : []
    }
    return options?.qualifying_methods?.length
        ? options.qualifying_methods
        : FALLBACK_QUALIFYING_METHODS
}

const FALLBACK_GUARANTOR_RELATIONSHIPS = [
    { id: 'parent', label: 'Parent' },
    { id: 'guardian', label: 'Guardian' },
    { id: 'relative', label: 'Relative' },
    { id: 'spouse', label: 'Spouse / partner' },
    { id: 'other', label: 'Other' },
]

function floorPlanOptionLabel(option: {
    name: string
    bed_count?: number | null
    bath_count?: number | null
}): string {
    const beds =
        option.bed_count != null && option.bed_count !== undefined
            ? `${option.bed_count} bed`
            : null
    const baths =
        option.bath_count != null && option.bath_count !== undefined
            ? `${option.bath_count} bath`
            : null
    const detail = [beds, baths].filter(Boolean).join(' / ')
    return detail ? `${option.name} (${detail})` : option.name
}

function answersMatchShowIf(answer: unknown, equals: string): boolean {
    const want = equals.trim().toLowerCase()
    if (answer === true) return ['yes', 'true', '1', 'on'].includes(want)
    if (answer === false) return ['no', 'false', '0', 'off'].includes(want)
    if (answer == null) return false
    const got = String(answer).trim().toLowerCase()
    if (got === want) return true
    if (['yes', 'true', '1', 'on'].includes(want)) {
        return ['yes', 'true', '1', 'on'].includes(got)
    }
    if (['no', 'false', '0', 'off'].includes(want)) {
        return ['no', 'false', '0', 'off'].includes(got)
    }
    return false
}

function fieldIsVisible(
    field: ResolvedApplicantField,
    currentAnswers: Record<string, unknown>,
): boolean {
    if (GUARANTOR_FIELD_ID_SET.has(field.field_id)) {
        return currentAnswers.qualifying_method === QUALIFYING_METHOD_GUARANTOR
    }
    if (field.show_if?.depends_on_field_id) {
        return answersMatchShowIf(
            currentAnswers[field.show_if.depends_on_field_id],
            field.show_if.equals,
        )
    }
    return true
}

function formatReviewValue(
    field: ResolvedApplicantField,
    answers: Record<string, unknown>,
    options: WorkbookApplicationResponse['options'],
    resolvedAddresses?: WorkbookApplicationResponse['resolved_addresses'],
): string {
    if (field.input_type === 'address' || field.field_id === 'current_address') {
        const resolved =
            resolvedAddresses?.[field.field_id] ||
            resolvedAddresses?.current_address
        return (
            formatInternationalAddressDisplay(resolved, answers) ||
            formatAddressDisplay(answers) ||
            '—'
        )
    }

    const value = answers[field.field_id]

    if (field.field_id === PREFERRED_PREMIUMS_FIELD_ID) {
        if (!Array.isArray(value)) return '—'
        if (value.length === 0) return 'No'
        const premiums = options?.premiums ?? []
        const labels = asPremiumIdList(value).map((id) => {
            const premium = premiums.find((item) => item.id === id)
            return premium?.name ?? String(id)
        })
        return labels.join(', ') || '—'
    }

    if (field.field_id === PREFERRED_ROOMMATES_FIELD_ID) {
        if (!Array.isArray(value)) return '—'
        const roommates = asRoommateList(value)
        if (!roommates.length) return 'None'
        return roommates
            .map((roommate) => roommate.name || roommate.email || 'Roommate')
            .join(', ')
    }

    if (!answerHasValue(value)) return '—'

    if (field.field_id === 'floor_plan_preference') {
        const unit = options?.unit_configs?.find((item) => item.id === String(value))
        return unit ? floorPlanOptionLabel(unit) : String(value)
    }

    if (
        field.field_id === 'qualifying_method' ||
        field.field_id === 'fallback_qualifying_method'
    ) {
        const methods = qualifyingMethodChoices(field.field_id, options)
        return methods.find((item) => item.id === value)?.label ?? String(value)
    }

    if (field.field_id === 'guarantor_relationship') {
        const relationships = options?.guarantor_relationships?.length
            ? options.guarantor_relationships
            : FALLBACK_GUARANTOR_RELATIONSHIPS
        return (
            relationships.find((item) => item.id === value)?.label ?? String(value)
        )
    }

    if (field.field_id === PET_ANIMAL_TYPE_FIELD_ID) {
        const types = options?.allowed_animal_types ?? []
        return types.find((item) => item.id === value)?.label ?? String(value)
    }

    if (field.field_id === PET_BREED_FIELD_ID) {
        const breeds = options?.allowed_dog_breeds ?? []
        return breeds.find((item) => item.id === value)?.label ?? String(value)
    }

    if (
        field.field_id === NO_LEGAL_MIDDLE_NAME_FIELD_ID ||
        field.field_id === NO_GUARANTOR_MIDDLE_NAME_FIELD_ID
    ) {
        return value === true ? 'Certified — no legal middle name' : '—'
    }

    if (field.input_type === 'boolean') {
        return value === true ? 'Yes' : value === false ? 'No' : '—'
    }

    if (field.input_type === 'date') {
        const date = parseDateAnswer(value)
        if (!date) return String(value)
        return date.toLocaleDateString()
    }

    return String(value)
}

const darkFieldSx = {
    '& .MuiInputLabel-root': { color: '#8b949e' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#46b083' },
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(139, 148, 158, 0.4)' },
        '&:hover fieldset': { borderColor: 'rgba(139, 148, 158, 0.7)' },
        '&.Mui-focused fieldset': { borderColor: '#46b083' },
    },
    '& .MuiSelect-icon': { color: '#8b949e' },
    '& .MuiFormHelperText-root': { color: '#8b949e' },
} as const

const darkSelectMenuProps = {
    PaperProps: {
        sx: {
            backgroundColor: '#2a2f35',
            color: '#fff',
            border: '1px solid rgba(139, 148, 158, 0.35)',
            '& .MuiMenuItem-root': {
                color: '#fff',
                '&:hover': {
                    backgroundColor: 'rgba(70, 176, 131, 0.18)',
                },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(70, 176, 131, 0.28)',
                    '&:hover': {
                        backgroundColor: 'rgba(70, 176, 131, 0.36)',
                    },
                },
            },
        },
    },
} as const

const primaryButtonSx = {
    backgroundColor: '#008C85',
    '&:hover': { backgroundColor: '#007c75' },
} as const

const outlinedButtonSx = {
    borderColor: '#46b083',
    color: '#46b083',
    '&:hover': {
        borderColor: '#008C85',
        backgroundColor: 'rgba(0, 140, 133, 0.1)',
    },
} as const

export const ApplicantWorkbook = ({
    leadId,
    remainderOnly: remainderOnlyProp = false,
}: ApplicantWorkbookProps) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const feeSessionId = searchParams.get('fee_session_id')
    const [application, setApplication] = useState<WorkbookApplicationResponse | null>(null)
    const [activeStep, setActiveStep] = useState(0)
    const [mode, setMode] = useState<WorkbookMode>('edit')
    const [answers, setAnswers] = useState<Record<string, unknown>>({})
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [uploadingFieldId, setUploadingFieldId] = useState('')
    const [error, setError] = useState('')
    const [clientSecret, setClientSecret] = useState<string | null>(null)
    const [checkoutStarting, setCheckoutStarting] = useState(false)
    const postPaymentSubmitStarted = useRef(false)
    const beforeFormCheckoutStarted = useRef(false)

    const remainderOnly = Boolean(
        remainderOnlyProp ||
            (application?.status === 'complete' && application?.remainder_open),
    )
    const configuredSections = application?.applicant_data?.sections ?? []
    const stepperSections = useMemo(() => {
        const sections = (
            application?.applicant_data?.sections ?? []
        ).filter(sectionIsAvailable)
        if (!remainderOnly) return sections
        const lifestyle = sections.filter((section) => section.id === 'lifestyle')
        if (lifestyle.length) return lifestyle
        return sections
            .map((section) => ({
                ...section,
                fields: section.fields.filter((field) =>
                    isLifestyleMatchField(field.field_id),
                ),
            }))
            .filter((section) => section.fields.length > 0)
    }, [application?.applicant_data?.sections, remainderOnly])

    const clearFeeSessionParam = useCallback(() => {
        if (!searchParams.has('fee_session_id')) return
        const next = new URLSearchParams(searchParams)
        next.delete('fee_session_id')
        setSearchParams(next, { replace: true })
    }, [searchParams, setSearchParams])

    useEffect(() => {
        let cancelled = false
        let pollTimer: ReturnType<typeof setTimeout> | undefined

        const load = async (isPoll = false) => {
            if (!isPoll) {
                setLoading(true)
                setError('')
            }
            try {
                const params = feeSessionId
                    ? { fee_session_id: feeSessionId }
                    : undefined
                const { data } = await axiosInstance.get<WorkbookApplicationResponse>(
                    `outreach/leads/${leadId}/application/`,
                    { params },
                )
                if (cancelled) return
                setApplication(data)
                setAnswers(data.answers ?? {})
                const loadedSections = data.applicant_data?.sections ?? []
                const hasIncompleteAvailable = loadedSections.some(
                    (section) =>
                        sectionIsAvailable(section) && !sectionIsComplete(section),
                )
                if (data.status === 'complete' && (data.remainder_open || remainderOnlyProp)) {
                    setMode('edit')
                } else if (data.status === 'complete' && hasIncompleteAvailable) {
                    setMode('edit')
                } else if (data.status === 'complete') {
                    setMode('waiting')
                } else if (feeSessionId && data.application_fee_paid) {
                    setMode(
                        data.application_fee_gate === 'before_form'
                            ? 'edit'
                            : 'review',
                    )
                } else if (
                    feeSessionId &&
                    !data.application_fee_paid &&
                    data.status !== 'complete'
                ) {
                    setMode('review')
                    pollTimer = setTimeout(() => {
                        void load(true)
                    }, 2000)
                }
                if (!isPoll) {
                    if (data.status === 'complete' && data.remainder_open) {
                        setActiveStep(0)
                    } else {
                        setActiveStep(
                            firstOpenSectionIndex(
                                loadedSections.filter(sectionIsAvailable),
                            ),
                        )
                    }
                }
            } catch {
                if (!cancelled && !isPoll) {
                    setError('Unable to load your application workbook.')
                }
            } finally {
                if (!cancelled && !isPoll) {
                    setLoading(false)
                }
            }
        }
        void load()
        return () => {
            cancelled = true
            if (pollTimer) clearTimeout(pollTimer)
        }
    }, [leadId, feeSessionId, remainderOnlyProp])

    useEffect(() => {
        if (stepperSections.length === 0) return
        if (activeStep >= stepperSections.length) {
            setActiveStep(firstOpenSectionIndex(stepperSections))
        }
    }, [activeStep, stepperSections])

    const currentSection = stepperSections[activeStep] ?? null

    const updateAnswers = useCallback((patch: Record<string, unknown>) => {
        setAnswers((prev) => {
            const next = { ...prev, ...patch }
            if (
                patch.qualifying_method &&
                patch.qualifying_method !== QUALIFYING_METHOD_GUARANTOR
            ) {
                for (const fieldId of GUARANTOR_FIELD_IDS) {
                    next[fieldId] = ''
                }
            }
            if (patch[PARKING_NEEDED_FIELD_ID] === false) {
                for (const fieldId of VEHICLE_DEPENDENT_FIELD_IDS) {
                    next[fieldId] = ''
                }
            }
            if (patch[HAS_PETS_FIELD_ID] === false) {
                next[PET_ANIMAL_TYPE_FIELD_ID] = ''
                next[PET_BREED_FIELD_ID] = ''
                next[PET_DETAILS_FIELD_ID] = ''
                next[PET_NAME_FIELD_ID] = ''
                next[PET_WEIGHT_FIELD_ID] = ''
                next[PET_PICTURE_FIELD_ID] = null
            }
            if (
                Object.prototype.hasOwnProperty.call(patch, PET_ANIMAL_TYPE_FIELD_ID) &&
                patch[PET_ANIMAL_TYPE_FIELD_ID] !== 'dog'
            ) {
                next[PET_BREED_FIELD_ID] = ''
            }
            for (const pair of MIDDLE_NAME_CERT_PAIRS) {
                if (patch[pair.certId] === true) {
                    next[pair.nameId] = ''
                }
                if (
                    typeof patch[pair.nameId] === 'string' &&
                    String(patch[pair.nameId]).trim()
                ) {
                    next[pair.certId] = false
                }
            }
            return next
        })
    }, [])

    const visibleFields = useMemo(() => {
        if (!currentSection) return []
        return currentSection.fields.filter((field) =>
            fieldIsVisible(field, answers),
        )
    }, [answers, currentSection])

    const buildSectionPatch = (): Record<string, unknown> => {
        const patch: Record<string, unknown> = {}
        for (const field of visibleFields) {
            if (field.input_type === 'address') {
                patch[field.field_id] = answers[field.field_id] ?? null
                continue
            }
            if (field.input_type === 'file') {
                if (!answerHasValue(answers[field.field_id])) {
                    patch[field.field_id] = null
                }
                continue
            }
            if (field.field_id === 'graduation_year' || field.input_type === 'number') {
                const raw = answers[field.field_id]
                if (raw === null || raw === undefined || raw === '') {
                    patch[field.field_id] = null
                } else {
                    patch[field.field_id] = Number(raw)
                }
                continue
            }
            if (
                field.field_id === PREFERRED_PREMIUMS_FIELD_ID ||
                field.field_id === PREFERRED_ROOMMATES_FIELD_ID
            ) {
                if (Array.isArray(answers[field.field_id])) {
                    patch[field.field_id] = answers[field.field_id]
                }
                continue
            }
            patch[field.field_id] = answers[field.field_id] ?? ''
            const certPair = middleNamePairForField(field.field_id)
            if (certPair && field.field_id === certPair.certId) {
                patch[field.field_id] = certifiesNoMiddleName(
                    answers,
                    certPair.certId,
                )
            }
            if (isLifestyleMatchField(field.field_id)) {
                const importanceKey = lifestyleImportanceKey(field.field_id)
                patch[importanceKey] = answers[importanceKey] ?? 'prefer'
            }
        }
        if (
            currentSection?.id === 'financial' &&
            answers.qualifying_method !== QUALIFYING_METHOD_GUARANTOR
        ) {
            for (const fieldId of GUARANTOR_FIELD_IDS) {
                patch[fieldId] = ''
            }
        }
        if (currentSection?.id === 'operational') {
            if (answers[PARKING_NEEDED_FIELD_ID] !== true) {
                for (const fieldId of VEHICLE_DEPENDENT_FIELD_IDS) {
                    patch[fieldId] = ''
                }
            }
            if (answers[HAS_PETS_FIELD_ID] !== true) {
                patch[PET_ANIMAL_TYPE_FIELD_ID] = ''
                patch[PET_BREED_FIELD_ID] = ''
                patch[PET_DETAILS_FIELD_ID] = ''
                patch[PET_NAME_FIELD_ID] = ''
                patch[PET_WEIGHT_FIELD_ID] = null
            } else if (answers[PET_ANIMAL_TYPE_FIELD_ID] !== 'dog') {
                patch[PET_BREED_FIELD_ID] = ''
            }
        }
        return patch
    }

    const validateCurrentSection = (): string | null => {
        if (!currentSection) return 'This section is unavailable.'
        for (const field of visibleFields) {
            const pair = middleNamePairForField(field.field_id)
            if (pair) {
                const middleResolved =
                    certifiesNoMiddleName(answers, pair.certId) ||
                    answerHasValue(answers[pair.nameId])
                if (!middleResolved) {
                    return pair.missingMessage
                }
                continue
            }
            const mustFill =
                field.required ||
                (remainderOnly && isLifestyleMatchField(field.field_id))
            if (!mustFill) continue
            if (field.input_type === 'address' || field.field_id === 'current_address') {
                if (!answerHasValue(answers[field.field_id] ?? answers.current_address)) {
                    return `Please complete ${field.question_text}.`
                }
                continue
            }
            if (
                field.field_id === PREFERRED_PREMIUMS_FIELD_ID ||
                field.field_id === PREFERRED_ROOMMATES_FIELD_ID
            ) {
                if (!listAnswerIsComplete(answers[field.field_id])) {
                    return `Please complete ${field.question_text}.`
                }
                continue
            }
            if (!answerHasValue(answers[field.field_id])) {
                return `Please complete ${field.question_text}.`
            }
        }
        return null
    }

    const saveSection = async (): Promise<boolean> => {
        const validationError = validateCurrentSection()
        if (validationError) {
            setError(validationError)
            return false
        }

        const patch = buildSectionPatch()
        setSaving(true)
        setError('')
        try {
            const { data } = await axiosInstance.patch<WorkbookApplicationResponse>(
                `outreach/leads/${leadId}/application/`,
                patch,
            )
            setApplication(data)
            setAnswers(data.answers ?? { ...answers, ...patch })
            return true
        } catch (err: unknown) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data
                    ?.error ?? 'Unable to save your answers.'
            setError(message)
            return false
        } finally {
            setSaving(false)
        }
    }

    const uploadWorkbookFile = async (fieldId: string, file: File) => {
        const form = new FormData()
        form.append('field_id', fieldId)
        form.append('file', file)
        setUploadingFieldId(fieldId)
        setError('')
        try {
            const { data } = await axiosInstance.post<WorkbookApplicationResponse>(
                `outreach/leads/${leadId}/application/files/`,
                form,
            )
            setApplication(data)
            setAnswers((prev) => ({
                ...prev,
                ...(data.answers ?? {}),
            }))
        } catch (err: unknown) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data
                    ?.error ?? 'Unable to upload that photo.'
            setError(message)
        } finally {
            setUploadingFieldId('')
        }
    }

    const handleSaveAndContinue = async () => {
        const saved = await saveSection()
        if (!saved) return
        setActiveStep((step) => nextAvailableIndex(stepperSections, step))
    }

    const handleReviewAndSubmit = async () => {
        const saved = await saveSection()
        if (!saved) return
        setMode('review')
    }

    const handleSaveRemainder = async () => {
        const saved = await saveSection()
        if (!saved) return
        setMode('review')
    }

    const submitWorkbook = useCallback(async () => {
        setSaving(true)
        setError('')
        try {
            const { data } = await axiosInstance.post<WorkbookApplicationResponse>(
                `outreach/leads/${leadId}/application/submit/`,
            )
            setApplication(data)
            setAnswers(data.answers ?? {})
            setClientSecret(null)
            setMode(data.remainder_open ? 'review' : 'waiting')
            setActiveStep(firstOpenSectionIndex(data.applicant_data?.sections ?? []))
            clearFeeSessionParam()
        } catch (err: unknown) {
            postPaymentSubmitStarted.current = false
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data
                    ?.error ?? 'Please complete all required fields before submitting.'
            setError(message)
        } finally {
            setSaving(false)
        }
    }, [clearFeeSessionParam, leadId])

    const startFeeCheckout = useCallback(async () => {
        setCheckoutStarting(true)
        setError('')
        try {
            const { data } = await axiosInstance.post<{
                client_secret: string
                application_fee_cents: number
            }>(`outreach/leads/${leadId}/application/fee-checkout/`)
            setClientSecret(data.client_secret)
            setMode('checkout')
        } catch (err: unknown) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data
                    ?.error ?? 'Unable to start application fee checkout.'
            setError(message)
            setMode(
                application?.application_fee_gate === 'before_form'
                    ? 'edit'
                    : 'review',
            )
        } finally {
            setCheckoutStarting(false)
        }
    }, [application?.application_fee_gate, leadId])

    const handleSubmitClick = async () => {
        if (application?.application_fee_requires_checkout) {
            await startFeeCheckout()
            return
        }
        await submitWorkbook()
    }

    useEffect(() => {
        if (
            !application?.application_fee_blocks_form ||
            application.status === 'complete' ||
            beforeFormCheckoutStarted.current ||
            feeSessionId
        ) {
            return
        }
        beforeFormCheckoutStarted.current = true
        void startFeeCheckout()
    }, [application, feeSessionId, startFeeCheckout])

    useEffect(() => {
        if (
            !feeSessionId ||
            !application ||
            application.status === 'complete' ||
            !application.application_fee_paid ||
            postPaymentSubmitStarted.current
        ) {
            return
        }
        if (application.application_fee_gate === 'before_form') {
            setMode('edit')
            clearFeeSessionParam()
            return
        }
        postPaymentSubmitStarted.current = true
        void submitWorkbook()
    }, [application, clearFeeSessionParam, feeSessionId, submitWorkbook])

    const reviewSections = useMemo(() => {
        return stepperSections
            .filter(sectionIsAvailable)
            .map((section) => {
                const fields = section.fields.filter((field) => {
                    if (!fieldIsVisible(field, answers)) return false
                    if (
                        field.input_type === 'address' ||
                        field.field_id === 'current_address'
                    ) {
                        return (
                            Boolean(formatAddressDisplay(answers)) ||
                            answerHasValue(answers[field.field_id] ?? answers.current_address)
                        )
                    }
                    if (
                        field.field_id === PREFERRED_PREMIUMS_FIELD_ID ||
                        field.field_id === PREFERRED_ROOMMATES_FIELD_ID
                    ) {
                        return Array.isArray(answers[field.field_id])
                    }
                    return answerHasValue(answers[field.field_id])
                })
                if (!fields.length) return null
                return { id: section.id, label: section.label, fields }
            })
            .filter(
                (
                    section,
                ): section is {
                    id: string
                    label: string
                    fields: ResolvedApplicantField[]
                } => section != null,
            )
    }, [answers, stepperSections])

    if (loading) {
        return <Typography sx={{ color: '#fff' }}>Loading application…</Typography>
    }

    if (error && !application) {
        return (
            <Typography sx={{ color: '#f87171' }}>
                {error}
            </Typography>
        )
    }

    if (!application || configuredSections.length === 0) {
        return (
            <Typography sx={{ color: '#8b949e' }}>
                No application workbook is configured for this property.
            </Typography>
        )
    }

    if (stepperSections.length === 0) {
        return (
            <Typography sx={{ color: '#8b949e' }}>
                No application sections are available yet.
            </Typography>
        )
    }

    const isLastStep = isLastAvailableSection(stepperSections, activeStep)
    const isSubmitted = application.status === 'complete'
    const feeCents = application.application_fee_cents ?? 0
    const feeRequiresCheckout = Boolean(application.application_fee_requires_checkout)
    const confirmingFeePayment = Boolean(feeSessionId) && !isSubmitted
    const requiredGateIndex = lastRequiredIndex(stepperSections)
    const allRequiredComplete =
        requiredGateIndex < 0 ||
        stepperSections
            .filter((section) => section.required)
            .every(sectionIsComplete)
    const showReviewContinue =
        !remainderOnly &&
        !isSubmitted &&
        allRequiredComplete &&
        (isLastStep || requiredGateIndex >= 0)

    const goToSection = (index: number) => {
        const section = stepperSections[index]
        if (!section || !sectionIsAvailable(section)) return
        setError('')
        setActiveStep(index)
        if (mode === 'checkout') return
        if (isSubmitted && !remainderOnly) {
            setMode(sectionIsEditable(section) ? 'edit' : 'waiting')
            return
        }
        if (mode === 'waiting' || mode === 'review') {
            setMode(isSubmitted && remainderOnly ? 'review' : 'edit')
        }
    }

    const sectionStepper = (
        <ApplicantWorkbookStepper
            sections={stepperSections}
            activeStep={activeStep}
            remainderOnly={remainderOnly}
            isSubmitted={isSubmitted}
            onGoToSection={goToSection}
        />
    )

    if (mode === 'checkout') {
        return (
            <Box>
                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                    Pay application fee
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e', mb: 1 }}>
                    {application.application_fee_gate === 'before_form'
                        ? 'An application fee is due before you can start the application.'
                        : 'An application fee is due before your application can be submitted.'}
                </Typography>
                <Typography variant="h5" sx={{ color: '#fff', mb: 3 }}>
                    {formatApplicationFee(feeCents)}
                </Typography>

                {error && (
                    <Typography sx={{ color: '#f87171', mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {!stripePromise ? (
                    <Typography sx={{ color: '#f87171' }}>
                        Payment is not configured for this environment.
                    </Typography>
                ) : !clientSecret || checkoutStarting ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            py: 4,
                        }}
                    >
                        <CircularProgress sx={{ color: '#46b083' }} />
                        <Typography sx={{ color: '#8b949e' }}>
                            Preparing secure checkout…
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            '& iframe': { width: '100% !important', border: 'none' },
                        }}
                    >
                        <EmbeddedCheckoutProvider
                            stripe={stripePromise}
                            options={{ clientSecret }}
                        >
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
                    <Button
                        variant="outlined"
                        disabled={checkoutStarting || saving}
                        onClick={() => {
                            setError('')
                            setClientSecret(null)
                            setMode('review')
                        }}
                        sx={outlinedButtonSx}
                    >
                        Back to review
                    </Button>
                </Box>
            </Box>
        )
    }

    if (mode === 'waiting') {
        const waitingSection =
            currentSection && sectionIsAvailable(currentSection)
                ? currentSection
                : [...stepperSections].reverse().find(sectionIsAvailable) ?? null
        const waitingFields = (waitingSection?.fields ?? []).filter((field) => {
            if (!fieldIsVisible(field, answers)) return false
            if (
                field.input_type === 'address' ||
                field.field_id === 'current_address'
            ) {
                return (
                    Boolean(formatAddressDisplay(answers)) ||
                    answerHasValue(
                        answers[field.field_id] ?? answers.current_address,
                    )
                )
            }
            if (
                field.field_id === PREFERRED_PREMIUMS_FIELD_ID ||
                field.field_id === PREFERRED_ROOMMATES_FIELD_ID
            ) {
                return Array.isArray(answers[field.field_id])
            }
            return answerHasValue(answers[field.field_id])
        })

        return (
            <Box>
                {sectionStepper}
                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                    You're all set for now
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e', mb: 3 }}>
                    {waitingSection?.id === 'lifestyle' &&
                    application.lifestyle_editable
                        ? "We've received this stage of your application. You can still update roommate preferences until your bed is confirmed."
                        : "We've received this stage of your application. We'll notify you if we need more information."}
                </Typography>
                {waitingSection && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            backgroundColor: '#1a1d21',
                            border: '1px solid',
                            borderColor: 'grey.700',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ color: '#fff', mb: 2 }}
                        >
                            {waitingSection.label}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                            }}
                        >
                            {waitingFields.map((field) => (
                                <Box key={field.field_id}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#8b949e',
                                            letterSpacing: '0.02em',
                                        }}
                                    >
                                        {field.question_text}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#fff',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {formatReviewValue(
                                            field,
                                            answers,
                                            application.options,
                                            application.resolved_addresses,
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}
                {waitingSection?.id === 'lifestyle' &&
                    sectionIsEditable(waitingSection) && (
                    <Button
                        variant="outlined"
                        sx={{ ...outlinedButtonSx, mt: 2 }}
                        onClick={() => {
                            const index = stepperSections.findIndex(
                                (section) => section.id === 'lifestyle',
                            )
                            if (index >= 0) goToSection(index)
                        }}
                    >
                        Update roommate preferences
                    </Button>
                )}
            </Box>
        )
    }

    if (mode === 'review') {
        return (
            <Box>
                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                    {remainderOnly
                        ? application.remainder_open
                            ? 'Lifestyle & roommate profile'
                            : 'Lifestyle profile saved'
                        : confirmingFeePayment
                          ? 'Submitting your application'
                          : 'Review your application'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e', mb: 3 }}>
                    {remainderOnly
                        ? application.remainder_open
                            ? 'These answers help us place compatible roommates. They are not part of your signed lease.'
                            : 'Thanks — your lifestyle answers are saved for roommate matching.'
                        : confirmingFeePayment
                          ? 'Payment received. Submitting your application…'
                          : 'Look everything over, then submit when you are ready.'}
                </Typography>

                {!isSubmitted && feeCents > 0 && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            mb: 3,
                            backgroundColor: '#1a1d21',
                            border: '1px solid',
                            borderColor: feeRequiresCheckout ? '#46b083' : 'grey.700',
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ color: '#fff', mb: 0.5 }}>
                            Application fee
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#8b949e' }}>
                            {feeRequiresCheckout
                                ? `An application fee of ${formatApplicationFee(
                                      feeCents,
                                  )} is due upon submission. Clicking Submit opens secure checkout.`
                                : `Application fee of ${formatApplicationFee(
                                      feeCents,
                                  )} has been paid.`}
                        </Typography>
                    </Paper>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    {reviewSections.map((section) => (
                        <Paper
                            key={section.id}
                            elevation={0}
                            sx={{
                                p: 3,
                                backgroundColor: '#1a1d21',
                                border: '1px solid',
                                borderColor: 'grey.700',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 2 }}>
                                {section.label}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {section.fields.map((field) => (
                                    <Box key={field.field_id}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#8b949e',
                                                letterSpacing: '0.02em',
                                            }}
                                        >
                                            {field.question_text}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ color: '#fff', wordBreak: 'break-word' }}
                                        >
                                            {formatReviewValue(
                                                field,
                                                answers,
                                                application.options,
                                                application.resolved_addresses,
                                            )}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {error && (
                    <Typography sx={{ color: '#f87171', mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {confirmingFeePayment && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <CircularProgress size={22} sx={{ color: '#46b083' }} />
                        <Typography sx={{ color: '#8b949e' }}>
                            Confirming payment and submitting…
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {(!isSubmitted || remainderOnly) && (
                        <Button
                            variant="outlined"
                            disabled={saving || checkoutStarting || confirmingFeePayment}
                            onClick={() => {
                                setError('')
                                setMode('edit')
                            }}
                            sx={outlinedButtonSx}
                        >
                            {remainderOnly ? 'Edit lifestyle' : 'Back to edit'}
                        </Button>
                    )}
                    {!isSubmitted && (
                        <Button
                            variant="contained"
                            disabled={saving || checkoutStarting || confirmingFeePayment}
                            onClick={() => void handleSubmitClick()}
                            sx={primaryButtonSx}
                        >
                            {feeRequiresCheckout
                                ? `Pay ${formatApplicationFee(feeCents)} & Submit`
                                : 'Submit'}
                        </Button>
                    )}
                </Box>
            </Box>
        )
    }

    return (
        <Box>
            {remainderOnly && (
                <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
                    Lifestyle profile for roommate matching
                </Typography>
            )}
            {sectionStepper}

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 2,
                    backgroundColor: '#1a1d21',
                    border: '1px solid',
                    borderColor: 'grey.700',
                }}
            >
                <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                    {currentSection?.label ?? ''}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {visibleFields.map((field) => {
                        const required = field.required
                        const label = field.question_text

                        if (
                            field.input_type === 'address' ||
                            field.field_id === 'current_address'
                        ) {
                            return (
                                <WorkbookAddressField
                                    key={field.field_id}
                                    fieldId={field.field_id}
                                    label={label}
                                    resolvedAddress={
                                        application?.resolved_addresses?.[
                                            field.field_id
                                        ] ?? null
                                    }
                                    required={required}
                                    onAnswersChange={updateAnswers}
                                />
                            )
                        }

                        const value = answers[field.field_id]
                        if (field.field_id === 'floor_plan_preference') {
                            const unitConfigs = application.options?.unit_configs ?? []
                            return (
                                <TextField
                                    key={field.field_id}
                                    select
                                    label={label}
                                    value={String(value ?? '')}
                                    onChange={(e) =>
                                        updateAnswers({ [field.field_id]: e.target.value })
                                    }
                                    SelectProps={{ MenuProps: darkSelectMenuProps }}
                                    required={required}
                                    fullWidth
                                    sx={darkFieldSx}
                                >
                                    <MenuItem value="">
                                        <em>Select…</em>
                                    </MenuItem>
                                    {unitConfigs.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {floorPlanOptionLabel(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )
                        }

                        if (field.field_id === PET_ANIMAL_TYPE_FIELD_ID) {
                            const types = application.options?.allowed_animal_types ?? []
                            if (types.length === 0) {
                                return (
                                    <Box key={field.field_id}>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#fff', mb: 0.5 }}
                                        >
                                            {label}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#8b949e' }}
                                        >
                                            {PET_NOT_ALLOWED_HELPER_TEXT}
                                        </Typography>
                                    </Box>
                                )
                            }
                            return (
                                <TextField
                                    key={field.field_id}
                                    select
                                    label={label}
                                    value={String(value ?? '')}
                                    onChange={(e) =>
                                        updateAnswers({ [field.field_id]: e.target.value })
                                    }
                                    SelectProps={{ MenuProps: darkSelectMenuProps }}
                                    required={required}
                                    fullWidth
                                    helperText={
                                        field.helper_text ||
                                        PET_ANIMAL_TYPE_HELPER_TEXT
                                    }
                                    sx={darkFieldSx}
                                >
                                    <MenuItem value="">
                                        <em>Select…</em>
                                    </MenuItem>
                                    {types.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )
                        }

                        if (field.field_id === PET_BREED_FIELD_ID) {
                            const breeds = application.options?.allowed_dog_breeds ?? []
                            return (
                                <TextField
                                    key={field.field_id}
                                    select
                                    label={label}
                                    value={String(value ?? '')}
                                    onChange={(e) =>
                                        updateAnswers({ [field.field_id]: e.target.value })
                                    }
                                    SelectProps={{ MenuProps: darkSelectMenuProps }}
                                    required={required}
                                    fullWidth
                                    helperText={
                                        field.helper_text || PET_BREED_HELPER_TEXT
                                    }
                                    sx={darkFieldSx}
                                >
                                    <MenuItem value="">
                                        <em>Select…</em>
                                    </MenuItem>
                                    {breeds.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )
                        }

                        if (field.field_id === PREFERRED_PREMIUMS_FIELD_ID) {
                            const premiums = application.options?.premiums ?? []
                            const selectedIds = asPremiumIdList(value)
                            const selected = new Set(selectedIds)
                            const noneSelected =
                                Array.isArray(value) && selectedIds.length === 0
                            return (
                                <FormControl
                                    key={field.field_id}
                                    component="fieldset"
                                    required={required}
                                    fullWidth
                                    sx={{ mb: 1 }}
                                >
                                    <FormLabel
                                        component="legend"
                                        sx={{ color: '#8b949e', mb: 0.5 }}
                                    >
                                        {label}
                                    </FormLabel>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#8b949e',
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        Select all that apply
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={noneSelected}
                                                    onChange={(event) => {
                                                        if (event.target.checked) {
                                                            updateAnswers({
                                                                [field.field_id]: [],
                                                            })
                                                        }
                                                    }}
                                                    sx={{
                                                        color: '#8b949e',
                                                        '&.Mui-checked': {
                                                            color: '#46b083',
                                                        },
                                                    }}
                                                />
                                            }
                                            label="No"
                                            sx={{ color: '#fff' }}
                                        />
                                        {premiums.map((premium) => (
                                            <FormControlLabel
                                                key={premium.id}
                                                control={
                                                    <Checkbox
                                                        checked={selected.has(
                                                            premium.id,
                                                        )}
                                                        onChange={(event) => {
                                                            const next = new Set(
                                                                selected,
                                                            )
                                                            if (event.target.checked) {
                                                                next.add(premium.id)
                                                            } else {
                                                                next.delete(premium.id)
                                                            }
                                                            updateAnswers({
                                                                [field.field_id]:
                                                                    Array.from(next),
                                                            })
                                                        }}
                                                        sx={{
                                                            color: '#8b949e',
                                                            '&.Mui-checked': {
                                                                color: '#46b083',
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={premium.name}
                                                sx={{ color: '#fff' }}
                                            />
                                        ))}
                                    </FormGroup>
                                </FormControl>
                            )
                        }

                        if (field.field_id === PREFERRED_ROOMMATES_FIELD_ID) {
                            const roommates = asRoommateList(value)
                            return (
                                <Box key={field.field_id} sx={{ mb: 1 }}>
                                    <Typography sx={{ color: '#fff', mb: 1 }}>
                                        {label}
                                        {required ? ' *' : ''}
                                    </Typography>
                                    {roommates.map((roommate, index) => (
                                        <Box
                                            key={`${index}-${roommate.email}`}
                                            sx={{
                                                mb: 1.5,
                                                p: 2,
                                                border:
                                                    '1px solid rgba(139, 148, 158, 0.35)',
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ color: '#fff', mb: 1 }}
                                            >
                                                Roommate {index + 1}
                                            </Typography>
                                            <TextField
                                                label="Name"
                                                value={roommate.name}
                                                onChange={(event) => {
                                                    const next = [...roommates]
                                                    next[index] = {
                                                        ...next[index],
                                                        name: event.target.value,
                                                    }
                                                    updateAnswers({
                                                        [field.field_id]: next,
                                                    })
                                                }}
                                                required={required}
                                                fullWidth
                                                sx={{ ...darkFieldSx, mb: 1 }}
                                            />
                                            <TextField
                                                label="Email"
                                                value={roommate.email}
                                                onChange={(event) => {
                                                    const next = [...roommates]
                                                    next[index] = {
                                                        ...next[index],
                                                        email: event.target.value,
                                                    }
                                                    updateAnswers({
                                                        [field.field_id]: next,
                                                    })
                                                }}
                                                required={required}
                                                fullWidth
                                                sx={{ ...darkFieldSx, mb: 1 }}
                                            />
                                            <TextField
                                                label="Phone"
                                                value={roommate.phone}
                                                onChange={(event) => {
                                                    const next = [...roommates]
                                                    next[index] = {
                                                        ...next[index],
                                                        phone: event.target.value,
                                                    }
                                                    updateAnswers({
                                                        [field.field_id]: next,
                                                    })
                                                }}
                                                required={required}
                                                fullWidth
                                                sx={{ ...darkFieldSx, mb: 1 }}
                                            />
                                            <Button
                                                onClick={() => {
                                                    updateAnswers({
                                                        [field.field_id]:
                                                            roommates.filter(
                                                                (_, i) => i !== index,
                                                            ),
                                                    })
                                                }}
                                                sx={{ color: '#8b949e' }}
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={() => {
                                            updateAnswers({
                                                [field.field_id]: [
                                                    ...roommates,
                                                    { name: '', email: '', phone: '' },
                                                ],
                                            })
                                        }}
                                        sx={{ color: '#46b083' }}
                                    >
                                        Add roommate
                                    </Button>
                                </Box>
                            )
                        }

                        if (
                            field.field_id === 'qualifying_method' ||
                            field.field_id === 'fallback_qualifying_method'
                        ) {
                            const methods = qualifyingMethodChoices(
                                field.field_id,
                                application.options,
                            )
                            const methodValue = String(
                                value ??
                                    (field.field_id === 'qualifying_method'
                                        ? methods[0]?.id ??
                                          QUALIFYING_METHOD_GUARANTOR
                                        : ''),
                            )
                            return (
                                <TextField
                                    key={field.field_id}
                                    select
                                    label={label}
                                    value={methodValue}
                                    onChange={(e) =>
                                        updateAnswers({ [field.field_id]: e.target.value })
                                    }
                                    SelectProps={{ MenuProps: darkSelectMenuProps }}
                                    required={required}
                                    fullWidth
                                    helperText={
                                        field.field_id === 'qualifying_method' &&
                                        methodValue === QUALIFYING_METHOD_SELF
                                            ? SELF_QUALIFY_HELPER
                                            : undefined
                                    }
                                    sx={darkFieldSx}
                                >
                                    {field.field_id === 'fallback_qualifying_method' && (
                                        <MenuItem value="">
                                            <em>Select…</em>
                                        </MenuItem>
                                    )}
                                    {methods.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )
                        }

                        if (field.field_id === 'guarantor_relationship') {
                            const relationships =
                                application.options?.guarantor_relationships
                                    ?.length
                                    ? application.options.guarantor_relationships
                                    : FALLBACK_GUARANTOR_RELATIONSHIPS
                            return (
                                <TextField
                                    key={field.field_id}
                                    select
                                    label={label}
                                    value={String(value ?? '')}
                                    onChange={(e) =>
                                        updateAnswers({ [field.field_id]: e.target.value })
                                    }
                                    SelectProps={{ MenuProps: darkSelectMenuProps }}
                                    required={required}
                                    fullWidth
                                    sx={darkFieldSx}
                                >
                                    <MenuItem value="">
                                        <em>Select…</em>
                                    </MenuItem>
                                    {relationships.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )
                        }

                        if (field.field_id === 'graduation_year') {
                            const yearValue =
                                value === null || value === undefined || value === ''
                                    ? ''
                                    : String(value)
                            return (
                                <TextField
                                    key={field.field_id}
                                    label={label}
                                    type="number"
                                    value={yearValue}
                                    onChange={(e) =>
                                        updateAnswers({ [field.field_id]: e.target.value })
                                    }
                                    required={required}
                                    fullWidth
                                    inputProps={{
                                        min: currentYear,
                                        max: currentYear + 15,
                                        step: 1,
                                    }}
                                    helperText={`Must be ${currentYear} or later`}
                                    sx={darkFieldSx}
                                />
                            )
                        }

                        if (field.input_type === 'number') {
                            const numberValue =
                                value === null || value === undefined || value === ''
                                    ? ''
                                    : String(value)
                            return (
                                <TextField
                                    key={field.field_id}
                                    label={label}
                                    type="number"
                                    value={numberValue}
                                    onChange={(e) =>
                                        updateAnswers({ [field.field_id]: e.target.value })
                                    }
                                    required={required}
                                    fullWidth
                                    inputProps={
                                        field.field_id === PET_WEIGHT_FIELD_ID
                                            ? { min: 0, step: 1 }
                                            : undefined
                                    }
                                    helperText={
                                        field.field_id === PET_WEIGHT_FIELD_ID
                                            ? field.helper_text ||
                                              PET_WEIGHT_HELPER_TEXT
                                            : field.helper_text
                                    }
                                    sx={darkFieldSx}
                                />
                            )
                        }

                        const middlePair = middleNamePairForField(field.field_id)
                        if (middlePair && field.field_id === middlePair.certId) {
                            return null
                        }

                        if (middlePair && field.field_id === middlePair.nameId) {
                            const certified = certifiesNoMiddleName(
                                answers,
                                middlePair.certId,
                            )
                            const certifyField = currentSection?.fields.find(
                                (item) => item.field_id === middlePair.certId,
                            )
                            return (
                                <Box key={field.field_id}>
                                    <TextField
                                        label={label}
                                        value={certified ? '' : String(value ?? '')}
                                        onChange={(e) =>
                                            updateAnswers({
                                                [field.field_id]: e.target.value,
                                            })
                                        }
                                        required={!certified}
                                        disabled={certified}
                                        fullWidth
                                        sx={darkFieldSx}
                                    />
                                    {certifyField ? (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={certified}
                                                    onChange={(event) =>
                                                        updateAnswers({
                                                            [middlePair.certId]:
                                                                event.target.checked,
                                                        })
                                                    }
                                                    sx={{
                                                        color: '#8b949e',
                                                        '&.Mui-checked': {
                                                            color: '#46b083',
                                                        },
                                                    }}
                                                />
                                            }
                                            label={certifyField.question_text}
                                            sx={{
                                                mt: 0.5,
                                                color: '#fff',
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '0.875rem',
                                                },
                                            }}
                                        />
                                    ) : null}
                                </Box>
                            )
                        }

                        if (field.input_type === 'boolean') {
                            return (
                                <TextField
                                    key={field.field_id}
                                    select
                                    label={label}
                                    value={
                                        value === true
                                            ? 'yes'
                                            : value === false
                                              ? 'no'
                                              : ''
                                    }
                                    onChange={(e) =>
                                        updateAnswers({
                                            [field.field_id]: e.target.value === 'yes',
                                        })
                                    }
                                    SelectProps={{ MenuProps: darkSelectMenuProps }}
                                    required={required}
                                    fullWidth
                                    helperText={
                                        field.field_id === HAS_PETS_FIELD_ID
                                            ? field.helper_text ||
                                              HAS_PETS_HELPER_TEXT
                                            : undefined
                                    }
                                    sx={darkFieldSx}
                                >
                                    <MenuItem value="">
                                        <em>Select…</em>
                                    </MenuItem>
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </TextField>
                            )
                        }

                        if (field.input_type === 'date') {
                            return (
                                <DatePicker
                                    key={field.field_id}
                                    label={label}
                                    value={parseDateAnswer(value)}
                                    onChange={(next) => {
                                        updateAnswers({
                                            [field.field_id]: formatDateAnswer(next),
                                        })
                                    }}
                                    slotProps={{
                                        textField: {
                                            required,
                                            fullWidth: true,
                                            sx: darkFieldSx,
                                        },
                                        openPickerButton: {
                                            sx: { color: '#8b949e' },
                                        },
                                    }}
                                />
                            )
                        }

                        if (field.input_type === 'select' && field.options?.length) {
                            return (
                                <Box key={field.field_id}>
                                    <TextField
                                        select
                                        label={label}
                                        value={String(value ?? '')}
                                        onChange={(e) =>
                                            updateAnswers({ [field.field_id]: e.target.value })
                                        }
                                        SelectProps={{ MenuProps: darkSelectMenuProps }}
                                        required={required || remainderOnly}
                                        fullWidth
                                        sx={darkFieldSx}
                                    >
                                        <MenuItem value="">
                                            <em>Select…</em>
                                        </MenuItem>
                                        {field.options.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    {isLifestyleMatchField(field.field_id) && (
                                        <LifestyleImportanceRadios
                                            fieldId={field.field_id}
                                            value={String(
                                                answers[lifestyleImportanceKey(field.field_id)] ??
                                                    'prefer',
                                            )}
                                            onChange={(next) =>
                                                updateAnswers({
                                                    [lifestyleImportanceKey(field.field_id)]: next,
                                                })
                                            }
                                        />
                                    )}
                                </Box>
                            )
                        }

                        if (field.input_type === 'file') {
                            const fileName = fileAnswerName(value)
                            const uploading = uploadingFieldId === field.field_id
                            return (
                                <Box key={field.field_id}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#fff', mb: 0.75 }}
                                    >
                                        {label}
                                        {required ? ' *' : ''}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            disabled={saving || uploading}
                                            sx={outlinedButtonSx}
                                        >
                                            {uploading
                                                ? 'Uploading…'
                                                : fileName
                                                  ? 'Replace photo'
                                                  : 'Upload photo'}
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,image/gif"
                                                hidden
                                                onChange={(event) => {
                                                    const nextFile =
                                                        event.target.files?.[0]
                                                    event.target.value = ''
                                                    if (nextFile) {
                                                        void uploadWorkbookFile(
                                                            field.field_id,
                                                            nextFile,
                                                        )
                                                    }
                                                }}
                                            />
                                        </Button>
                                        {fileName ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#8b949e' }}
                                            >
                                                {fileName}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    {field.helper_text ? (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#8b949e',
                                                mt: 0.5,
                                                display: 'block',
                                            }}
                                        >
                                            {field.helper_text}
                                        </Typography>
                                    ) : null}
                                </Box>
                            )
                        }

                        return (
                            <TextField
                                key={field.field_id}
                                label={label}
                                type={
                                    field.input_type === 'email'
                                        ? 'email'
                                        : field.input_type === 'phone'
                                          ? 'tel'
                                          : 'text'
                                }
                                value={String(value ?? '')}
                                onChange={(e) =>
                                    updateAnswers({ [field.field_id]: e.target.value })
                                }
                                required={required}
                                fullWidth
                                multiline={field.input_type === 'textarea'}
                                minRows={field.input_type === 'textarea' ? 3 : undefined}
                                sx={darkFieldSx}
                            />
                        )
                    })}
                </Box>
            </Paper>

            {error && (
                <Typography sx={{ color: '#f87171', mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {!remainderOnly && (
                    <Button
                        variant="outlined"
                        disabled={
                            prevAvailableIndex(stepperSections, activeStep) ===
                                activeStep || saving
                        }
                        onClick={() => {
                            setError('')
                            setActiveStep((step) =>
                                prevAvailableIndex(stepperSections, step),
                            )
                        }}
                        sx={outlinedButtonSx}
                    >
                        Back
                    </Button>
                )}
                {!isLastStep && currentSection && (
                    <Button
                        variant={showReviewContinue ? 'outlined' : 'contained'}
                        disabled={saving}
                        onClick={() => void handleSaveAndContinue()}
                        sx={showReviewContinue ? outlinedButtonSx : primaryButtonSx}
                    >
                        Save and Continue
                    </Button>
                )}
                {isLastStep && remainderOnly && (
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={() => void handleSaveRemainder()}
                        sx={primaryButtonSx}
                    >
                        Save lifestyle answers
                    </Button>
                )}
                {showReviewContinue && (
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={() => void handleReviewAndSubmit()}
                        sx={primaryButtonSx}
                    >
                        Review and continue
                    </Button>
                )}
                {isLastStep && !remainderOnly && isSubmitted && (
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={async () => {
                            const saved = await saveSection()
                            if (!saved) return
                            setMode('waiting')
                        }}
                        sx={primaryButtonSx}
                    >
                        Save
                    </Button>
                )}
            </Box>
        </Box>
    )
}
